/**
 * 매뉴얼 커맨드 팔레트 (`Ctrl/⌘ + K`).
 *
 * 문서가 늘면 목차로는 못 찾는다 — 검색이 실제 진입로다. 매칭 규칙은 전부
 * `manualSearchIndex.ts`(순수 로직)에 있고, 이 파일은 키·포커스·표시만 맡는다.
 *
 * 모달 셸은 `@safetyhub/ui`의 `Modal` — 포탈·포커스 트랩·Esc·스크롤 잠금·opener 복원을
 * base가 보장하므로 여기서 다시 구현하지 않는다([[feedback-modal-zindex-policy]]).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '@safetyhub/ui';
import { MANUAL_SECTION_LABELS, manualDocs, manuals } from './manualContent';
import {
  buildSearchIndex,
  searchManual,
  splitHighlights,
  type ManualSearchIndex,
  type ManualSearchResult,
} from './manualSearchIndex';

/**
 * 인덱스는 처음 필요해질 때 한 번만 만들고 계속 쓴다.
 * 질의마다 다시 만들면 전 문서 본문 파싱이 **타자 한 번마다** 돈다.
 */
let cachedIndex: ManualSearchIndex | null = null;
function getSearchIndex(): ManualSearchIndex {
  if (!cachedIndex) cachedIndex = buildSearchIndex(manualDocs);
  return cachedIndex;
}

const MANUAL_TITLES = new Map(manuals.map((manual) => [manual.id, manual.title]));

/** 사용자가 글을 쓰고 있는 요소 — 여기서는 단축키를 가로채면 안 된다. */
function isEditableTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el.tagName !== 'string') return false;
  if (el.isContentEditable) return true;
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName);
}

/** 결과 한 줄의 출처 표시 — 어느 매뉴얼의 어느 섹션인지 (문서 제목만으로는 모른다). */
function ResultBreadcrumb({ result }: { result: ManualSearchResult }) {
  const manualTitle = MANUAL_TITLES.get(result.manual) ?? result.manual;
  const sectionLabel = MANUAL_SECTION_LABELS[result.section] ?? result.section;
  return (
    <span className="text-xs text-gray-400">
      {manualTitle} › {sectionLabel}
      {result.heading && result.field !== 'title' && <> › {result.heading}</>}
    </span>
  );
}

/** 스니펫 하이라이트 — 검색어를 HTML로 주입하지 않고 조각 배열로 그린다. */
function Snippet({ result }: { result: ManualSearchResult }) {
  const parts = splitHighlights(result.snippet, result.highlights);
  return (
    <p className="mt-0.5 line-clamp-2 text-xs text-gray-600">
      {parts.map((part, i) =>
        part.hit ? (
          <mark key={i} className="rounded bg-yellow-100 px-0.5 text-gray-900">
            {part.text}
          </mark>
        ) : (
          <span key={i}>{part.text}</span>
        ),
      )}
    </p>
  );
}

export interface ManualSearchProps {
  open: boolean;
  /** `Ctrl/⌘ + K` 감지 시 호출 (열림 상태는 호출부가 소유). */
  onOpen: () => void;
  onClose: () => void;
}

export default function ManualSearch({ open, onOpen, onClose }: ManualSearchProps) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  const results = useMemo(
    () => (open ? searchManual(getSearchIndex(), query) : []),
    [open, query],
  );

  // 열릴 때마다 초기화 — 직전 질의가 남아 있으면 "왜 이 결과지"가 된다.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
    }
  }, [open]);

  // 질의가 바뀌면 선택은 첫 줄로.
  useEffect(() => {
    setActive(0);
  }, [query]);

  // `Ctrl/⌘ + K` — 팔레트가 닫혀 있어도 잡아야 하므로 document 레벨.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      if (e.key !== 'k' && e.key !== 'K') return;
      // 입력 중에는 가로채지 않는다 — 단, 팔레트 자신의 입력창은 예외.
      if (isEditableTarget(e.target) && !panelRef.current?.contains(e.target as Node)) return;
      e.preventDefault();
      if (!open) onOpen();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onOpen]);

  // 화살표로 내려간 항목이 스크롤 밖으로 나가지 않게.
  useEffect(() => {
    listRef.current
      ?.querySelector(`[data-result-index="${active}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [active, results]);

  const go = (result: ManualSearchResult) => {
    navigate(`/manual/${result.manual}/${result.section}/${result.slug}`);
    onClose();
  };

  const onPanelKeyDown = (e: React.KeyboardEvent) => {
    if (results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (i + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const target = results[active] ?? results[0];
      if (target) go(target);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="매뉴얼 검색"
      size="xl"
      showCloseButton={false}
      initialFocusRef={inputRef}
      // ⛔ 예외(이유① — 패널 스크롤 보유): 아래 panelClassName이 `max-h-[80vh]`를 들고 있어
      // base `bodyScroll`이 붙이는 `max-h-full`과 **같은 property로 부딪힌다**(승자가 Tailwind
      // 출력 순서에 달린다). 결과 표시는 본문 안 `max-h-[55vh] overflow-y-auto`가 이미 소유한다.
      bodyScroll={false}
      panelClassName="max-h-[80vh] overflow-hidden"
    >
      <div ref={panelRef} onKeyDown={onPanelKeyDown}>
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="문서 제목·내용 검색 (예: 값 연결, rangeMap)"
          aria-label="매뉴얼 검색어"
          className="w-full rounded-control border border-gray-300 px-3 py-2 text-sm focus:border-red-400 focus:outline-none"
        />

        <div className="mt-3 max-h-[55vh] overflow-y-auto">
          {query.trim() === '' ? (
            <p className="px-1 py-6 text-center text-sm text-gray-500">
              찾을 말을 입력하세요. 제목·요약·본문을 함께 찾습니다.
            </p>
          ) : results.length === 0 ? (
            <p className="px-1 py-6 text-center text-sm text-gray-500">
              “{query.trim()}”에 대한 결과가 없습니다. 더 짧은 말로 찾아보세요.
            </p>
          ) : (
            <ul ref={listRef} className="space-y-0.5">
              {results.map((result, i) => (
                <li key={result.key}>
                  <button
                    type="button"
                    data-result-index={i}
                    onClick={() => go(result)}
                    onMouseEnter={() => setActive(i)}
                    aria-current={i === active}
                    className={`block w-full rounded-card px-3 py-2 text-left ${
                      i === active ? 'bg-red-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <span className="block text-sm font-medium text-gray-900">{result.title}</span>
                    <ResultBreadcrumb result={result} />
                    <Snippet result={result} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <p className="mt-3 border-t border-gray-100 pt-2 text-xs text-gray-400">
          ↑↓ 이동 · Enter 열기 · Esc 닫기
        </p>
      </div>
    </Modal>
  );
}
